/**
 * Weakness Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { toast } from 'sonner';
import { useWeaknessStore } from '@/store/weakness.store';
import { useTenantStore } from '@/store/tenant.store';

export class WeaknessIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch weaknesses and update store
     */
    async fetchWeaknesses(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}, options = {}) {
        const store = useWeaknessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                columnFilters,
                ...(options.assetId ? { assetId: options.assetId } : {}),
            });

            const { data, pagination, uniqueValues } = response;
            store.setWeaknesses(data || []);

            // Map pagination from backend response
            if (pagination) {
                store.setPagination({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPagination({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create weakness and refresh list
     */
    async createWeakness(data) {
        const store = useWeaknessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            toast.success(i18next.t('integrationToasts:weaknessCreatedSuccessfully', { defaultValue: 'Weakness created successfully' }));
            store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update weakness
     */
    async updateWeakness(id, data) {
        const store = useWeaknessStore.getState();

        store.setLoading(true);

        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:weaknessUpdatedSuccessfully', { defaultValue: 'Weakness updated successfully' }));
            store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete weaknesses
     */
    async bulkDeleteWeaknesses(ids) {
        const store = useWeaknessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('integrationToasts:selectedWeaknessesDeletedSuccessfully', { defaultValue: 'Selected weaknesses deleted successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete weakness
     */
    async deleteWeakness(id) {
        const store = useWeaknessStore.getState();

        store.setLoading(true);

        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:weaknessDeletedSuccessfully', { defaultValue: 'Weakness deleted successfully' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Weakness not found',
        });
    }

    async listForAsset(assetId, opts = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) return { success: false, data: [] };
        try {
            const pageSize = opts.pageSize ?? 50;
            const response = await this.service.getAll({
                tenantId,
                assetId,
                page: 1,
                pageSize,
                limit: pageSize,
                sortBy: opts.sortBy ?? 'updatedAt',
                sortOrder: opts.sortOrder ?? 'desc',
            });
            return { success: true, data: response.data || [] };
        } catch (error) {
            return { success: false, data: [], error: this._getErrorMessage(error) };
        }
    }
}
